/**
 * Routing + permission account helpers shared by middleware and hooks.
 */
import { PERMISSIONS } from './constants/permissions';
import {
  ROUTE_PERMISSION_RULES,
  ROUTES_SILENT_PERMISSION_DENY,
} from './routes';

const PUBLIC_RULES = [
  { method: 'POST', pattern: 'auth/log-in' },
  { method: 'POST', pattern: 'auth/forgot' },
  { method: 'POST', pattern: 'auth/confirm-forgot' },
  { method: 'POST', pattern: 'auth/reset-password' },
  { method: 'POST', pattern: 'auth/refresh-auth' },
  { method: 'GET', pattern: 'auth/callback' },
  { method: 'POST', pattern: 'auth/claim-grant' },
  { method: 'POST', pattern: 'google/auth/claim-grant' },
  { method: 'POST', pattern: 'ms/auth/claim-grant' },
  { method: 'POST', pattern: 'google/auth/refresh-auth' },
  { method: 'POST', pattern: 'ms/auth/refresh-auth' },
  { method: 'GET', pattern: 'google/auth/signin' },
  { method: 'GET', pattern: 'ms/auth/signin' },
  { method: 'GET', pattern: 'accounts/by-token' },
];

function segs(path) {
  return path.split('/').filter(Boolean);
}

export function matchPattern(pattern, path) {
  const p = segs(pattern);
  const a = segs(path);
  if (p.length !== a.length) return false;
  for (let i = 0; i < p.length; i++) {
    if (!p[i].startsWith(':') && p[i] !== a[i]) return false;
  }
  return true;
}

export function normalizeApiPath(url) {
  if (!url || typeof url !== 'string') return '';
  try {
    const base = url.split('?')[0];
    if (base.includes('://')) {
      const u = new URL(base);
      let p = u.pathname || '';
      if (p.startsWith('/api')) p = p.slice(4);
      return p.replaceAll(/^\/+|\/+$/g, '');
    }
    return base.replaceAll(/^\/+|\/+$/g, '');
  } catch {
    return url.split('?')[0].replaceAll(/^\/+|\/+$/g, '');
  }
}

export function isPublicApiRoute(method, url) {
  const path = normalizeApiPath(url);
  const m = (method || 'GET').toUpperCase();
  return PUBLIC_RULES.some((r) => r.method === m && matchPattern(r.pattern, path));
}

function isSilentPermissionDeny(method, path) {
  const m = (method || 'GET').toUpperCase();
  return ROUTES_SILENT_PERMISSION_DENY.some(
    (route) => route.method === m && matchPattern(route.pattern, path),
  );
}

export function resolveRequiredPermission(method, path) {
  const m = (method || 'GET').toUpperCase();
  for (const rule of ROUTE_PERMISSION_RULES) {
    if (rule.method === m && matchPattern(rule.pattern, path)) {
      const permissions = rule.permissions?.length
        ? rule.permissions
        : rule.permission
          ? [rule.permission]
          : [];
      return {
        permissions,
        permission: permissions[0],
        showModal: !isSilentPermissionDeny(m, path),
      };
    }
  }
  return null;
}

function rowKey(item) {
  if (!item) return '';
  if (typeof item === 'string') return item;
  return (
    item.accessKey ||
    item.key ||
    item.slug ||
    item.permission?.accessKey ||
    item.permission?.key ||
    ''
  );
}

function extractCompanyRolePermissionKeys(account) {
  if (!Array.isArray(account?.accountCompanyRoles)) return [];

  return account.accountCompanyRoles.flatMap((accountCompanyRole) => {
    const permissions = accountCompanyRole?.companyRole?.companyRolePermissions;
    if (!Array.isArray(permissions)) return [];
    return permissions.map((row) => rowKey(row?.permission || row)).filter(Boolean);
  });
}

function dedupe(arr) {
  return [...new Set(arr.map(String))];
}

export function extractPermissionKeys(account) {
  if (!account || typeof account !== 'object') return [];
  if (Array.isArray(account.permissionKeys)) {
    return dedupe(account.permissionKeys.filter(Boolean));
  }
  if (Array.isArray(account.permissionAccessKeys)) {
    return dedupe(account.permissionAccessKeys.filter(Boolean));
  }
  const list = account.permissions ?? account.accountPermissions;
  if (Array.isArray(list)) {
    return dedupe(list.map(rowKey).filter(Boolean));
  }
  const companyRolePermissionKeys = extractCompanyRolePermissionKeys(account);
  if (companyRolePermissionKeys.length > 0) {
    return dedupe(companyRolePermissionKeys);
  }
  return [];
}

export function hasPermission(keys, required) {
  if (!required) return true;
  if (keys.has('*')) return true;
  return keys.has(required);
}

export function hasAnyPermission(keys, requiredPermissions = []) {
  if (!requiredPermissions.length) return true;
  return requiredPermissions.some((permission) => hasPermission(keys, permission));
}

/** Enforce permission gate whenever an account context exists. */
export function permissionGateShouldEnforce(account) {
  return Boolean(account && typeof account === 'object');
}

const ATR_JOB_CREATION_BYPASS_PERMISSIONS = new Set([
  PERMISSIONS.CREATE_UPDATE_JOB,
  PERMISSIONS.ACCESS_QUESTIONNAIRES,
  PERMISSIONS.VIEW_ALL_ACCOUNTS,
  PERMISSIONS.MANAGE_APPLICATIONS,
]);

export function shouldBypassCreateJobPermissionForRecruitmentRequest(
  recruitmentRequestId,
  requiredPermission,
) {
  return (
    Boolean(recruitmentRequestId) &&
    ATR_JOB_CREATION_BYPASS_PERMISSIONS.has(requiredPermission)
  );
}

/** ATR / recruitment-request id from a job listing or hiring-plan listing item. */
export function resolveRecruitmentRequestIdFromJobListingItem(job) {
  if (!job || typeof job !== 'object') return null;

  const nestedJob = job.job && typeof job.job === 'object' ? job.job : null;
  const candidates = [
    job.recruitmentRequestId,
    job.recruitmentRequest?.id,
    Array.isArray(job.recruitmentRequests) ? job.recruitmentRequests[0]?.id : null,
    nestedJob?.recruitmentRequestId,
    nestedJob?.recruitmentRequest?.id,
    Array.isArray(nestedJob?.recruitmentRequests)
      ? nestedJob.recruitmentRequests[0]?.id
      : null,
  ];

  const match = candidates.find((value) => value != null && value !== '');
  return match != null ? match : null;
}

function extractJobIdFromApiPath(path) {
  if (!path || typeof path !== 'string') return null;
  const match = /^jobs\/([^/]+)/.exec(path);
  const id = match?.[1];
  if (!id || id === 'new' || id === 'v2') return null;
  return id;
}

/** Extract jobId from an API URL's query string (e.g. GET applications?jobId=abc). */
function extractJobIdFromApiQueryParams(rawUrl) {
  if (!rawUrl || typeof rawUrl !== 'string') return null;
  try {
    const qs = rawUrl.includes('?') ? rawUrl.split('?')[1] : '';
    return new URLSearchParams(qs).get('jobId') || null;
  } catch {
    return null;
  }
}

/**
 * Extract jobId from the browser page's pathname.
 * Handles /jobs/:jobId/... and /jobs/edit/:jobId/...
 */
function extractJobIdFromPagePathname() {
  try {
    const parts = window.location.pathname.split('/').filter(Boolean);
    const jobsIdx = parts.indexOf('jobs');
    if (jobsIdx === -1) return null;
    const candidate = parts[jobsIdx + 1];
    if (!candidate || candidate === 'edit') {
      // /jobs/edit/:jobId/...
      return parts[jobsIdx + 2] || null;
    }
    return candidate;
  } catch {
    return null;
  }
}

function extractRecruitmentRequestIdFromPageUrl() {
  try {
    return new URLSearchParams(window.location.search).get('recruitmentRequestId') || null;
  } catch {
    return null;
  }
}

export function resolveRecruitmentRequestIdForJobPermission(state, body, path, rawUrl) {
  const jobRecruitmentRequestMap = state?.createJob?.jobRecruitmentRequestMap || {};

  const jobId =
    body?.jobId ||
    extractJobIdFromApiPath(path) ||
    extractJobIdFromApiQueryParams(rawUrl) ||
    extractJobIdFromPagePathname();

  return (
    body?.recruitmentRequestId ||
    state?.createJob?.recruitmentRequestId ||
    state?.createJob?.jobData?.values?.recruitmentRequestId ||
    (jobId ? jobRecruitmentRequestMap[jobId] : null) ||
    extractRecruitmentRequestIdFromPageUrl() ||
    null
  );
}
